import { useDeferredValue, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Message, Vide } from "@cabinet/charte";
import { dossiersApi, ErreurApi, planDuDossierApi, type Compte } from "../api/client";
import { EtiquetteSens, JetonsDeCompte } from "../composants/Elements";
import { formaterNombre } from "../domaine/formats";
import { useSession } from "../session";

/** Recherche accent et casse ignorés, comme dans la grille. */
const replier = (texte: string) =>
  texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * Plan d'un dossier (EF-0204) : le référentiel en vigueur, augmenté des
 * sous-comptes que le dossier y ouvre.
 *
 * Le plan de référence ne se modifie pas d'ici : le dossier n'enregistre que
 * ses ajouts, et une nouvelle version du référentiel lui profite d'elle-même.
 * Ouvrir un sous-compte sous un compte mouvementable fait de celui-ci un
 * niveau d'agrégation ; l'écran le rappelle avant, et le constate après.
 */
export function PagePlanDuDossier() {
  const { id = "" } = useParams();
  const { peut } = useSession();
  const cache = useQueryClient();
  const [recherche, setRecherche] = useState("");
  const [propresSeulement, setPropresSeulement] = useState(false);
  const [formulaire, setFormulaire] = useState<{ numero: string; libelle: string; codeDeTva: string } | null>(null);
  const [retour, setRetour] = useState<{ type: "succes" | "erreur" | "avertissement"; texte: string } | null>(null);
  const rechercheDifferee = useDeferredValue(recherche);

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const plan = useQuery({ queryKey: ["plan-dossier", id, "ecran"], queryFn: () => planDuDossierApi.plan(id) });

  const rafraichir = () => {
    void cache.invalidateQueries({ queryKey: ["plan-dossier", id] });
  };
  const signaler = (echec: unknown) =>
    setRetour({ type: "erreur", texte: echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti." });

  const ouverture = useMutation({
    mutationFn: (demande: { numero: string; libelle: string; codeDeTva: string }) =>
      planDuDossierApi.ouvrir(id, { ...demande, codeDeTva: demande.codeDeTva.trim() || null }),
    onSuccess: (ouvert) => {
      rafraichir();
      setFormulaire(null);
      setRetour(
        ouvert.parentDejaMouvemente
          ? {
              type: "avertissement",
              texte: `Sous-compte ${ouvert.compte} ouvert. Son parent portait déjà des écritures : elles y restent, mais les suivantes iront sur ses sous-comptes.`,
            }
          : { type: "succes", texte: `Sous-compte ${ouvert.compte} — ${ouvert.libelle} ouvert.` },
      );
    },
    onError: signaler,
  });
  const suppression = useMutation({
    mutationFn: (numero: string) => planDuDossierApi.supprimer(id, numero),
    onSuccess: rafraichir,
    onError: signaler,
  });
  const renommage = useMutation({
    mutationFn: ({ compte, libelle }: { compte: Compte; libelle: string }) =>
      planDuDossierApi.modifier(id, compte.numero, { libelle, codeDeTva: compte.codeDeTva }),
    onSuccess: rafraichir,
    onError: signaler,
  });

  const comptes = useMemo(() => {
    const terme = replier(rechercheDifferee.trim());
    return (plan.data?.comptes ?? []).filter(
      (compte) =>
        (!propresSeulement || compte.propre) &&
        (terme === "" || compte.numero.startsWith(terme) || replier(compte.libelle).includes(terme)),
    );
  }, [plan.data, rechercheDifferee, propresSeulement]);

  const parents = useMemo(() => new Map((plan.data?.comptes ?? []).map((c) => [c.numero, c] as const)), [plan.data]);
  const parentPropose = formulaire != null && formulaire.numero.length >= 2 ? parents.get(formulaire.numero.slice(0, -1)) : undefined;
  const peutOrganiser = peut("CREER_DOSSIER");

  function preparerSous(compte: Compte) {
    setRetour(null);
    // Premier numéro libre sous ce compte : 5141, puis 5142…
    let suffixe = 1;
    while (parents.has(`${compte.numero}${suffixe}`) && suffixe < 9) suffixe += 1;
    setFormulaire({ numero: `${compte.numero}${suffixe}`, libelle: "", codeDeTva: "" });
  }

  const erreur = dossier.error ?? plan.error;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Plan du dossier"
        description={
          plan.data != null
            ? `Référentiel ${plan.data.referentiel}, version ${plan.data.version}, augmenté de ${formaterNombre(plan.data.nombreDeSousComptes)} sous-compte(s) propre(s) au dossier. Le plan de référence ne se modifie pas d'ici : ses mises à jour profitent d'elles-mêmes au dossier.`
            : "Le référentiel en vigueur, augmenté des sous-comptes du dossier."
        }
        actions={
          peutOrganiser ? (
            <button
              type="button"
              className={formulaire == null ? "principal" : undefined}
              onClick={() => setFormulaire(formulaire == null ? { numero: "", libelle: "", codeDeTva: "" } : null)}
            >
              {formulaire == null ? "Ouvrir un sous-compte" : "Annuler"}
            </button>
          ) : undefined
        }
      />

      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}
      {erreur != null && (
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      )}

      {formulaire != null && (
        <form
          className="carte formulaire-sous-compte"
          onSubmit={(e) => {
            e.preventDefault();
            ouverture.mutate(formulaire);
          }}
        >
          <div className="champs">
            <label className="champ">
              Numéro
              <input
                value={formulaire.numero}
                inputMode="numeric"
                autoFocus
                onChange={(e) => setFormulaire({ ...formulaire, numero: e.target.value.replace(/\D/g, "") })}
                placeholder="5141"
                required
              />
            </label>
            <label className="champ">
              Libellé
              <input
                value={formulaire.libelle}
                onChange={(e) => setFormulaire({ ...formulaire, libelle: e.target.value })}
                placeholder="Banque Populaire — compte courant"
                required
              />
            </label>
            <label className="champ">
              Code de TVA (facultatif)
              <input
                value={formulaire.codeDeTva}
                onChange={(e) => setFormulaire({ ...formulaire, codeDeTva: e.target.value.toUpperCase() })}
                placeholder={parentPropose?.codeDeTva ?? "hérité du parent"}
              />
            </label>
            <button type="submit" className="principal" disabled={ouverture.isPending}>
              Ouvrir
            </button>
          </div>
          <p className="aide discret-texte">
            {formulaire.numero.length < 2
              ? "Un sous-compte prend le numéro de son parent, suivi d'un chiffre."
              : parentPropose != null
                ? `Sous ${parentPropose.numero} — ${parentPropose.libelle} : il en hérite le sens, les caractéristiques et le code de TVA.${
                    parentPropose.mouvementable
                      ? ` ${parentPropose.numero} deviendra un niveau d'agrégation : la saisie se fera sur ses sous-comptes.`
                      : ""
                  }`
                : `Le compte ${formulaire.numero.slice(0, -1)} n'existe pas dans le plan : ouvrir d'abord le parent.`}
          </p>
        </form>
      )}

      {plan.data != null && plan.data.ecartes.length > 0 && (
        <Message type="avertissement" titre="Sous-comptes écartés par la version en vigueur du référentiel">
          <ul style={{ margin: "6px 0 0" }}>
            {plan.data.ecartes.map((e) => (
              <li key={e.numero}>
                {e.numero} — {e.libelle} : {e.motif}.
              </li>
            ))}
          </ul>
        </Message>
      )}

      <div className="filtres-plan">
        <input
          type="search"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Numéro ou libellé — 514, banque…"
          aria-label="Rechercher un compte"
        />
        <label className="discret-texte">
          <input type="checkbox" checked={propresSeulement} onChange={(e) => setPropresSeulement(e.target.checked)} />{" "}
          Sous-comptes du dossier seulement
        </label>
        <span className="decompte">{formaterNombre(comptes.length)} comptes</span>
      </div>

      {plan.isPending && <Chargement quoi="le plan du dossier" />}
      {plan.data != null && comptes.length === 0 && <Vide>Aucun compte ne correspond à ce filtre.</Vide>}

      {comptes.length > 0 && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Libellé</th>
                <th>Sens normal</th>
                <th>Caractéristiques</th>
                {peutOrganiser && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {comptes.map((compte) => (
                <tr key={compte.numero} className={compte.mouvementable ? undefined : "agregation"}>
                  <td className="numero-compte">{compte.numero}</td>
                  <td className="libelle-compte" data-niveau={Math.min(compte.niveau, 5)}>
                    {compte.libelle} {compte.propre && <Etiquette variante="info">Dossier</Etiquette>}
                  </td>
                  <td>
                    <EtiquetteSens sens={compte.sensNormal} />
                  </td>
                  <td>
                    <JetonsDeCompte compte={compte} />
                  </td>
                  {peutOrganiser && (
                    <td>
                      <div className="jetons">
                        {compte.niveau >= 2 && compte.niveau < 12 && (
                          <button type="button" className="tertiaire" onClick={() => preparerSous(compte)}>
                            Sous-compte
                          </button>
                        )}
                        {compte.propre && (
                          <>
                            <button
                              type="button"
                              className="tertiaire"
                              onClick={() => {
                                const libelle = window.prompt(`Nouveau libellé de ${compte.numero} :`, compte.libelle);
                                if (libelle != null && libelle.trim() !== "") renommage.mutate({ compte, libelle: libelle.trim() });
                              }}
                            >
                              Renommer
                            </button>
                            <button
                              type="button"
                              className="tertiaire"
                              onClick={() => {
                                setRetour(null);
                                if (window.confirm(`Supprimer le sous-compte ${compte.numero} — ${compte.libelle} ?`)) {
                                  suppression.mutate(compte.numero);
                                }
                              }}
                            >
                              Supprimer
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
